import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import BotCatalogBadge from '../components/botFarmacia/BotCatalogBadge';
import PedidoDeliveryNote, { parseDelivery, stripDeliveryStructuredSegments } from '../components/PedidoDeliveryNote';
import {
  consumeBotFarmaciaCatalogResult,
  markBotFarmaciaCatalogReturn
} from '../utils/botFarmaciaCatalog';
import {
  extractCatalogTokenFromText,
  messageReferencesPreviousCatalog,
  prepareBotMessageText
} from '../utils/botFarmaciaMessage';
import {
  clearAtencionSimSession,
  applyAtencionBotCatalogSelections,
  createAtencionBotCatalogToken,
  fetchAtencionBotHealth,
  sendAtencionBotAttachment,
  sendAtencionBotAudio,
  sendAtencionSimMessage
} from '../api/atencionBot';
import {
  fetchAtencionConversacion,
  fetchAtencionConversaciones,
  fetchAtencionPedido,
  loadAtencionPedidosForFarmacia,
  loadAtencionSucursalesOperativas,
  confirmarAtencionPedidoFarmacia,
  updateAtencionPedidoSucursal
} from '../api/atencionPedidos';
import { fetchAsociacionByPhone } from '../api/association';
import {
  ATENCION_BOT_BASE_URL,
  isValidSimPhoneDigits,
  normalizeSimPhoneDigits,
  simPhoneDigitsToE164
} from '../config/atencionBot';
import { useAuth } from '../context/AuthContext';
import {
  formatWhatsappPhone,
  isClientIdentityResolved,
  persistContactDisplayName,
  resolveSimClientDisplayName
} from '../utils/chatwootContactDisplay';
import CenteredMessage from '../components/CenteredMessage';
import { CwIconChevronPrev, CwListSearch } from '../components/conversaciones/CwUiParts';
import '../styles/conversaciones-premium.css';
import '../styles/bot-farmacia-premium.css';

const BOT_AVATAR = '/pulseoneblue.png';

const INTENT_META = {
  GREETING: { label: 'Saludo', tone: 'muted', icon: '👋' },
  AWAITING_DNI: { label: 'Esperando DNI', tone: 'warning', icon: '🪪' },
  AWAITING_REGISTRATION: { label: 'Registro', tone: 'warning', icon: '📝' },
  IDENTITY_RESOLVED: { label: 'Identificado', tone: 'success', icon: '✓' },
  MED_SEARCH: { label: 'Búsqueda', tone: 'progress', icon: '🔍' },
  STOCK_PRICE: { label: 'Precio / stock', tone: 'progress', icon: '📦' },
  LIST_PICK: { label: 'Selección', tone: 'progress', icon: '🔢' },
  PEDIDO_CREATE: { label: 'Pedido', tone: 'success', icon: '🛒' },
  FAQ: { label: 'Consulta', tone: 'muted', icon: 'ℹ️' },
  HUMAN_AGENT: { label: 'Operador', tone: 'warning', icon: '🧑‍💼' },
  RETURN_TO_BOT: { label: 'Volver al bot', tone: 'muted', icon: '🤖' },
  CHRONIC_PROGRAM: { label: 'Crónicos', tone: 'warning', icon: '💊' },
  RECIPE_CONFIRM: { label: 'Receta', tone: 'progress', icon: '📋' },
  RECIPE_ATTACHMENT: { label: 'Receta', tone: 'progress', icon: '📎' },
  CATALOG_CART: { label: 'Carrito', tone: 'success', icon: '🛒' },
  OUTSIDE_SCHEDULE_NOTICE: { label: 'Aviso fuera del horario programado', tone: 'success', icon: '⏰' },
  AUDIO_INPUT: { label: 'Audio', tone: 'progress', icon: '🎤' },
  PEDIDO_STATUS_NOTIFY: { label: 'Estado pedido', tone: 'success', icon: '📬' },
  PEDIDO_STATUS: { label: 'Consulta pedido', tone: 'progress', icon: '📋' },
  THANK_YOU: { label: 'Agradecimiento', tone: 'muted', icon: '🙏' },
  POST_PEDIDO: { label: 'Post pedido', tone: 'muted', icon: '💬' },
  FARMACLUB: { label: 'FarmaClub', tone: 'warning', icon: '⭐' },
  OPTICA_ORTOPEDIA: { label: 'Óptica / ortopedia', tone: 'warning', icon: '👓' }
};

function intentDisplayMeta(intent) {
  const key = String(intent || '').trim();
  if (!key) return null;
  return (
    INTENT_META[key] || {
      label: key
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
      tone: 'muted',
      icon: null
    }
  );
}

function shortSearchTerm(term) {
  const t = String(term || '').trim();
  if (t.length > 36) return `${t.slice(0, 33)}…`;
  return t;
}

function getPedidoStructuredNotas(rawNotas, pedidoDetail) {
  const raw = String(rawNotas || '').trim();
  if (!raw) return { plainText: '', hasStructuredDelivery: false, sucursal: '' };

  const parsed = parseDelivery(raw);
  const plainText = stripDeliveryStructuredSegments(raw);
  const direccion = String(pedidoDetail?.direccion_envio || '').trim();
  const metodo = String(pedidoDetail?.metodo_entrega || '').trim().toLowerCase();
  const hasStructuredDelivery = Boolean(
    direccion ||
    parsed.domicilio ||
    parsed.coords ||
    metodo === 'domicilio'
  );

  return {
    plainText,
    hasStructuredDelivery,
    sucursal: parsed.sucursal || ''
  };
}

const SIM_SKIP_REASON_ES = {
  human_handoff_active:
    'Esta conversación está con un operador humano. Anita está en pausa; escribí «volver al asistente» para retomar el bot.',
  locked: 'El bot está procesando otro mensaje. Intentá de nuevo en unos segundos.',
  empty_message: 'Mensaje vacío: no hay nada para que Anita responda.',
  not_incoming: 'Solo se procesan mensajes entrantes del paciente.',
  missing_conversation_id: 'No se pudo identificar la conversación.'
};

function formatSimSkipMessage(reason) {
  const key = String(reason || '').trim();
  if (SIM_SKIP_REASON_ES[key]) return SIM_SKIP_REASON_ES[key];
  if (!key) return 'Mensaje no procesado por el bot.';
  return `Mensaje no procesado: ${key.replace(/_/g, ' ')}.`;
}

function hasPermission(user, code) {
  return Boolean((user?.permissions || []).includes(code));
}

function createSessionId() {
  return `pulse-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function newConversationId() {
  return `local-${createSessionId()}`;
}

function toSimSessionId(conversationId) {
  const id = String(conversationId || '').trim();
  return id.startsWith('local-') ? id.slice(6) : id;
}

function formatPhoneDisplay(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits || /^0+$/.test(digits)) return null;
  if (digits.length === 10) return `+54 9 ${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone || null;
}

function formatProductLabel(raw) {
  const name = String(raw || '')
    .trim()
    .replace(/\*/g, '');
  if (!name) return '';
  return name.length > 34 ? `${name.slice(0, 34)}…` : name;
}

function buildSimContactSubtitle({ phone, pedidoId }) {
  const phoneLabel = formatWhatsappPhone(phone) || formatPhoneDisplay(phone);
  const parts = [];
  if (phoneLabel) parts.push(phoneLabel);
  if (pedidoId) parts.push(`#${pedidoId}`);
  // ID local (local-pulse-…) omitido del header: confunde al operador.
  return parts.join(' · ') || 'Simulación local';
}

function normalizeListPhoneE164(phone, fallbackE164) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length >= 10) return `+549${digits.slice(-10)}`;
  return fallbackE164;
}

function conversationRowDisplayName(
  conv,
  { isActive = false, activeDisplayName = '', fallbackPhoneE164 = '' } = {}
) {
  if (isActive) {
    const activeName = String(activeDisplayName || '').trim();
    if (activeName) return activeName;
  }

  const session = conv?.session || {};
  const fromSession = resolveSimClientDisplayName({
    profile: null,
    sessionClientIdentity: session?.client_identity,
    phone: normalizeListPhoneE164(conv?.phone, fallbackPhoneE164),
    session,
    identityResolved: isClientIdentityResolved(session)
  });
  if (fromSession) return fromSession;

  if (conv?.last_pedido_id) return `Pedido #${conv.last_pedido_id}`;
  const product = formatProductLabel(conv?.producto_preview);
  if (product) return product;
  return 'Cliente simulado';
}

function conversationListSubtitle(conv) {
  const digits = normalizeSimPhoneDigits(conv?.phone);
  return buildSimContactSubtitle({
    phone: digits ? simPhoneDigitsToE164(digits) : null,
    pedidoId: conv?.last_pedido_id
  });
}

function conversationChatTitle({ contactDisplayName }) {
  return contactDisplayName || 'Cliente simulado';
}

function buildIncomingMessageAuthor({ displayName, phoneRaw }) {
  const name = String(displayName || '').trim() || 'Cliente simulado';
  const digits = String(phoneRaw || '').replace(/\D/g, '').slice(-10);
  return digits ? `${name} · ${digits}` : name;
}

function isPedidoMessageDeliveredToFarmacia(msg, { pedidoId, farmaciaEstado }) {
  const msgPedidoId = Number(msg?.meta?.pedidoId);
  const activeId = Number(pedidoId);
  if (!Number.isFinite(msgPedidoId) || msgPedidoId <= 0) return false;
  if (!Number.isFinite(activeId) || activeId <= 0) return false;
  if (msgPedidoId !== activeId) return false;
  return Boolean(String(farmaciaEstado || '').trim());
}

function mapDbMessagesToUi(mensajes) {
  return (mensajes || []).map((msg) => ({
    id: `db-${msg.id}`,
    role: msg.direction === 'in' ? 'user' : 'bot',
    text: msg.body,
    meta: {
      intent: msg.meta?.intent,
      pedidoId: msg.meta?.pedido_id
    },
    at: new Date(msg.created_at)
  }));
}

function conversationPreview(conv) {
  const raw = String(conv?.last_message || conv?.producto_preview || 'Sin mensajes').trim();
  return raw.length > 72 ? `${raw.slice(0, 72)}…` : raw;
}

function formatBotText(text) {
  const safe = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return safe
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/(https?:\/\/[^\s<]+)/g, (url) => {
      if (/\/bot-farmacia\/t\//i.test(url)) return '';
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="crm-bf-msg-link">${url}</a>`;
    })
    .replace(/\n/g, '<br />');
}

function formatTime(date) {
  try {
    return new Date(date).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}


const PEDIDO_STATUS_LABELS = {
  borrador: { label: 'Borrador', tone: 'progress' },
  confirmado: { label: 'Confirmado', tone: 'success' },
  cancelado: { label: 'Cancelado', tone: 'warning' }
};

const FARMACIA_ESTADO_LABELS = {
  pedido_generado_farmacia: { label: 'Farmacia', tone: 'success' },
  en_preparacion: { label: 'Preparación', tone: 'progress' },
  en_camino: { label: 'Cadete', tone: 'progress' },
  entregado: { label: 'Entregado', tone: 'success' },
  cancelado: { label: 'Cancelado', tone: 'warning' }
};

function pedidoStatusMeta(status, farmaciaEstado) {
  if (farmaciaEstado && FARMACIA_ESTADO_LABELS[farmaciaEstado]) {
    return FARMACIA_ESTADO_LABELS[farmaciaEstado];
  }
  const key = String(status || 'borrador').toLowerCase();
  return PEDIDO_STATUS_LABELS[key] || { label: key, tone: 'muted' };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
}

const RECIPE_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf'
];

const AUDIO_ATTACHMENT_TYPES = [
  'audio/ogg',
  'audio/opus',
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'audio/m4a',
  'audio/wav',
  'audio/webm',
  'audio/aac'
];

function audioExtensionFromMime(mimeType) {
  const mime = String(mimeType || '').toLowerCase();
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  if (mime.includes('m4a') || mime.includes('mp4')) return 'm4a';
  if (mime.includes('wav')) return 'wav';
  return 'ogg';
}

function nextMessageId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function buildOptimisticPedido(result, conversationId) {
  const pedidoId = Number(result?.pedido_id);
  if (!Number.isFinite(pedidoId) || pedidoId <= 0) return null;
  const selected = result?.session?.selected_product;
  const farmaciaEstado =
    result?.farmacia_estado ||
    (result?.intent === 'PEDIDO_CREATE' ? 'pedido_generado_farmacia' : null);
  return {
    id: pedidoId,
    conversation_id: conversationId,
    status: 'confirmado',
    farmacia_estado: farmaciaEstado,
    farmacia_ingresado_at: new Date().toISOString(),
    items: selected
      ? [
        {
          idproducto: selected.idproducto,
          producto: selected.producto,
          presentacion: selected.presentacion || null,
          cantidad: 1,
          precio_unitario: selected.precio ?? null,
          stock_q: selected.stock_q ?? null
        }
      ]
      : []
  };
}

function deriveFlowPhase({ lastIntent, session, lastPedidoId, shouldHandoff }) {
  if (shouldHandoff) return { key: 'handoff', label: 'Derivación humana', tone: 'warning' };
  if (lastPedidoId || session?.last_pedido_id) {
    return { key: 'pedido', label: 'Pedido ingresado en Farmacia', tone: 'success' };
  }
  if (session?.selected_product && lastIntent === 'PEDIDO_CREATE') {
    return { key: 'confirm', label: 'Esperando confirmación', tone: 'active' };
  }
  if (Array.isArray(session?.last_padron_hits) && session.last_padron_hits.length > 1) {
    return { key: 'pick', label: 'Elegir del listado', tone: 'progress' };
  }
  if (session?.selected_product) {
    return { key: 'product', label: 'Producto seleccionado', tone: 'active' };
  }
  if (['MED_SEARCH', 'STOCK_PRICE', 'LIST_PICK'].includes(lastIntent)) {
    return { key: 'search', label: 'Consulta de producto', tone: 'progress' };
  }
  if (lastIntent === 'GREETING') return { key: 'greet', label: 'Conversación iniciada', tone: 'muted' };
  return { key: 'idle', label: 'Listo para chatear', tone: 'muted' };
}

function formatDateTimeCompact(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatPedidoPrice(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function BfPedidoProductHero({ producto, presentacion, precio, codigo, creado }) {
  return (
    <div className="crm-bf-pedido-hero">
      <div className="crm-bf-pedido-hero__glow" aria-hidden="true" />
      <div className="crm-bf-pedido-hero__head">
        <span className="crm-bf-pedido-hero__eyebrow">Producto</span>
        <h3 className="crm-bf-pedido-hero__name">{producto}</h3>
        {presentacion ? (
          <p className="crm-bf-pedido-hero__presentacion">{presentacion}</p>
        ) : null}
      </div>
      <div className="crm-bf-pedido-hero__price">
        <span className="crm-bf-pedido-hero__price-label">Precio mostrador</span>
        <strong className="crm-bf-pedido-hero__price-value">
          ${formatPedidoPrice(precio)}
        </strong>
      </div>
      <div className="crm-bf-pedido-hero__foot">
        {codigo ? (
          <span className="crm-bf-pedido-hero__chip">
            <span className="crm-bf-pedido-hero__chip-lbl">Código</span>
            <span className="crm-bf-pedido-hero__chip-val">{codigo}</span>
          </span>
        ) : null}
        {creado ? (
          <span className="crm-bf-pedido-hero__chip">
            <span className="crm-bf-pedido-hero__chip-lbl">Creado</span>
            <span className="crm-bf-pedido-hero__chip-val">{creado}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function BfPedidoItemsList({ items, creado }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="crm-bf-pedido-hero crm-bf-pedido-hero--multi">
      <div className="crm-bf-pedido-hero__glow" aria-hidden="true" />
      <div className="crm-bf-pedido-hero__head">
        <span className="crm-bf-pedido-hero__eyebrow">Productos del pedido</span>
        <h3 className="crm-bf-pedido-hero__name">{list.length} productos</h3>
      </div>
      <ul
        className="crm-bf-pedido-items"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '8px'
        }}
      >
        {list.map((it, idx) => (
          <li
            key={`${it.idproducto || it.producto || 'item'}-${idx}`}
            className="crm-bf-pedido-items__row"
          >
            <div className="crm-bf-pedido-items__info">
              <span className="crm-bf-pedido-items__name">{it.producto || 'Producto'}</span>
              {it.presentacion ? (
                <span className="crm-bf-pedido-items__pres">{it.presentacion}</span>
              ) : null}
              {it.idproducto ? (
                <span className="crm-bf-pedido-items__code">Cód. {it.idproducto}</span>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {creado ? (
        <div className="crm-bf-pedido-hero__foot" style={{ gridTemplateColumns: 'auto' }}>
          <span className="crm-bf-pedido-hero__chip">
            <span className="crm-bf-pedido-hero__chip-lbl">Creado</span>
            <span className="crm-bf-pedido-hero__chip-val">{creado}</span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

const PROFILE_ACCORDION_SECTION = {
  producto: 'producto',
  pedido: 'pedido',
  sesion: 'sesion'
};

export default function BotFarmacia() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canAccess = hasPermission(user, 'ver_bot_farmacia');

  const [activeConvId, setActiveConvId] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [botOnline, setBotOnline] = useState(null);
  const [error, setError] = useState('');
  const [lastIntent, setLastIntent] = useState(null);
  const [lastPedidoId, setLastPedidoId] = useState(null);
  const [shouldHandoff, setShouldHandoff] = useState(false);
  const [sessionSnapshot, setSessionSnapshot] = useState({});
  const [listSearch, setListSearch] = useState('');
  const [pedidoDetail, setPedidoDetail] = useState(null);
  const [sessionPedidos, setSessionPedidos] = useState([]);
  const [savedMessages, setSavedMessages] = useState([]);
  const [loadingPedidoPanel, setLoadingPedidoPanel] = useState(false);
  const [conversationList, setConversationList] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightConvId, setHighlightConvId] = useState(null);
  const deepLinkConvHandledRef = useRef(false);
  const conversationListRef = useRef(null);
  const [confirmingFarmacia, setConfirmingFarmacia] = useState(false);
  const [catalogApplying, setCatalogApplying] = useState(false);
  const [catalogOpening, setCatalogOpening] = useState(false);
  const [sucursalesOperativas, setSucursalesOperativas] = useState([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [sucursalSavingPedidoId, setSucursalSavingPedidoId] = useState(null);
  const [profileOpenSection, setProfileOpenSection] = useState(PROFILE_ACCORDION_SECTION.pedido);
  const [simClientProfile, setSimClientProfile] = useState(null);
  const [newConvModalOpen, setNewConvModalOpen] = useState(false);
  const [newConvPhoneDraft, setNewConvPhoneDraft] = useState('');
  const [newConvPhoneError, setNewConvPhoneError] = useState('');
  const [activeSimPhoneDigits, setActiveSimPhoneDigits] = useState('');
  const [listCollapsed, setListCollapsed] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState({});

  const activeSimPhoneE164 = useMemo(() => {
    const conv = conversationList.find((c) => c.conversation_id === activeConvId);
    const digits = normalizeSimPhoneDigits(conv?.phone) || activeSimPhoneDigits;
    return simPhoneDigitsToE164(digits);
  }, [conversationList, activeConvId, activeSimPhoneDigits]);

  const openProfileAccordionSection = useCallback(
    (section) => (event) => {
      event.preventDefault();
      setProfileOpenSection((prev) => {
        if (section === PROFILE_ACCORDION_SECTION.pedido) {
          return PROFILE_ACCORDION_SECTION.pedido;
        }
        if (section === PROFILE_ACCORDION_SECTION.sesion) {
          return prev === PROFILE_ACCORDION_SECTION.sesion
            ? PROFILE_ACCORDION_SECTION.pedido
            : PROFILE_ACCORDION_SECTION.sesion;
        }
        return prev === section ? null : section;
      });
    },
    []
  );

  const listRef = useRef(null);
  const listSearchRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const catalogReturnHandledRef = useRef(false);

  const convLabel = activeConvId || '';
  const simSessionId = useMemo(
    () => (activeConvId ? toSimSessionId(activeConvId) : ''),
    [activeConvId]
  );

  const flowPhase = useMemo(
    () =>
      deriveFlowPhase({
        lastIntent,
        session: sessionSnapshot,
        lastPedidoId,
        shouldHandoff
      }),
    [lastIntent, sessionSnapshot, lastPedidoId, shouldHandoff]
  );

  const activeConvFarmaciaEstado = useMemo(() => {
    if (pedidoDetail?.farmacia_estado) return pedidoDetail.farmacia_estado;
    const conv = conversationList.find((c) => c.conversation_id === activeConvId);
    return conv?.last_pedido_farmacia_estado || null;
  }, [pedidoDetail?.farmacia_estado, conversationList, activeConvId]);

  const clientIdentityResolved = useMemo(
    () => isClientIdentityResolved(sessionSnapshot),
    [sessionSnapshot]
  );

  const activeContactPhone = useMemo(() => {
    const conv = conversationList.find((c) => c.conversation_id === activeConvId);
    const digits = normalizeSimPhoneDigits(conv?.phone) || activeSimPhoneDigits;
    return simPhoneDigitsToE164(digits);
  }, [conversationList, activeConvId, activeSimPhoneDigits]);

  const simContactDisplayName = useMemo(
    () =>
      resolveSimClientDisplayName({
        profile: simClientProfile,
        sessionClientIdentity: sessionSnapshot?.client_identity,
        phone: activeContactPhone,
        session: sessionSnapshot,
        identityResolved: clientIdentityResolved
      }),
    [
      simClientProfile,
      sessionSnapshot,
      activeContactPhone,
      clientIdentityResolved
    ]
  );

  const filteredConversations = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return conversationList;
    return conversationList.filter((conv) => {
      const isActive = chatOpen && conv.conversation_id === activeConvId;
      const rowTitle = conversationRowDisplayName(conv, {
        isActive,
        activeDisplayName: simContactDisplayName,
        fallbackPhoneE164: activeSimPhoneE164
      });
      const haystack = [
        rowTitle,
        conv.phone,
        formatWhatsappPhone(conv?.phone || activeSimPhoneE164),
        conv.last_message,
        conv.producto_preview,
        conv.last_pedido_id != null ? `#${conv.last_pedido_id}` : ''
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [conversationList, listSearch, simContactDisplayName, activeSimPhoneE164, chatOpen, activeConvId]);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!canAccess || !clientIdentityResolved) {
      setSimClientProfile(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchAsociacionByPhone(activeContactPhone);
        if (!cancelled) setSimClientProfile(profile);
      } catch {
        if (!cancelled) setSimClientProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, activeContactPhone, clientIdentityResolved]);

  useEffect(() => {
    if (!clientIdentityResolved || !simContactDisplayName) return;
    persistContactDisplayName(activeContactPhone, simContactDisplayName);
  }, [simContactDisplayName, activeContactPhone, clientIdentityResolved]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, scrollToBottom]);

  const checkHealth = useCallback(async () => {
    try {
      await fetchAtencionBotHealth();
      setBotOnline(true);
      setError('');
    } catch {
      setBotOnline(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) return;
    checkHealth();
  }, [canAccess, checkHealth]);

  useEffect(() => {
    if (!canAccess) return undefined;
    let cancelled = false;
    setLoadingSucursales(true);
    loadAtencionSucursalesOperativas()
      .then((rows) => {
        if (!cancelled) setSucursalesOperativas(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setSucursalesOperativas([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSucursales(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canAccess]);

  const sucursalOptions = useMemo(
    () => (sucursalesOperativas || []).map((s) => ({
      codigo: String(s?.codigo || '').trim(),
      nombre: String(s?.nombre || '').trim(),
      direccion: String(s?.direccion || '').trim(),
      localidad: String(s?.localidad || '').trim(),
      latitud: Number.isFinite(Number(s?.latitud)) ? Number(s.latitud) : null,
      longitud: Number.isFinite(Number(s?.longitud)) ? Number(s.longitud) : null,
      cadeteria: Number(s?.cadeteria || 0) === 1 ? 1 : 0
    })).filter((s) => s.codigo && s.nombre),
    [sucursalesOperativas]
  );

  const applyPedidosFarmaciaToState = useCallback((pedidosFarmacia) => {
    const farmaciaByConv = new Map();
    const farmaciaByPedidoId = new Map();
    for (const p of pedidosFarmacia || []) {
      const estado = p.estado || 'pedido_generado_farmacia';
      if (p.conversation_id) farmaciaByConv.set(p.conversation_id, estado);
      if (p.atencion_pedido_id) farmaciaByPedidoId.set(Number(p.atencion_pedido_id), { ...p, estado });
    }

    const mergePedidoFarmaciaCard = (pedido) => {
      if (!pedido?.id) return pedido;
      const card = farmaciaByPedidoId.get(Number(pedido.id));
      if (!card) return pedido;
      return {
        ...pedido,
        farmacia_estado: card.estado || pedido.farmacia_estado,
        status: pedido.status || 'confirmado',
        metodo_entrega: card.metodo_entrega || pedido.metodo_entrega,
        direccion_envio: card.direccion_envio ?? pedido.direccion_envio,
        sucursal_codigo: card.sucursal_codigo ?? pedido.sucursal_codigo,
        sucursal_nombre: card.sucursal_nombre ?? pedido.sucursal_nombre,
        sucursal: card.sucursal ?? pedido.sucursal,
        latitud: card.latitud ?? pedido.latitud,
        longitud: card.longitud ?? pedido.longitud
      };
    };

    setConversationList((prev) =>
      (prev || []).map((c) => ({
        ...c,
        last_pedido_farmacia_estado:
          c.last_pedido_farmacia_estado || farmaciaByConv.get(c.conversation_id) || null
      }))
    );

    setPedidoDetail((prev) => {
      if (!prev?.id) return prev;
      return mergePedidoFarmaciaCard(prev);
    });

    setSessionPedidos((prev) =>
      (prev || []).map((p) => mergePedidoFarmaciaCard(p))
    );
  }, []);

  const syncConversations = useCallback(async () => {
    if (!canAccess) return;
    try {
      const conversaciones = await fetchAtencionConversaciones({ limit: 80 });
      setConversationList((prev) => {
        const prevById = new Map((prev || []).map((c) => [c.conversation_id, c]));
        return conversaciones.map((c) => ({
          ...c,
          last_pedido_farmacia_estado:
            c.last_pedido_farmacia_estado ||
            prevById.get(c.conversation_id)?.last_pedido_farmacia_estado ||
            null
        }));
      });
    } catch {
      // sync silencioso
    } finally {
      setLoadingConversations(false);
    }
  }, [canAccess]);

  const syncPedidosFarmacia = useCallback(async () => {
    if (!canAccess) return;
    try {
      const pedidosFarmacia = await loadAtencionPedidosForFarmacia({ limit: 150 });
      applyPedidosFarmaciaToState(pedidosFarmacia);
    } catch {
      // sync silencioso
    }
  }, [canAccess, applyPedidosFarmaciaToState]);

  const syncFarmaciaState = useCallback(async () => {
    await Promise.all([syncConversations(), syncPedidosFarmacia()]);
  }, [syncConversations, syncPedidosFarmacia]);

  const patchConversationListFarmacia = useCallback((conversationId, pedidoId, farmaciaEstado) => {
    const convId = String(conversationId || '').trim();
    const pedidoNum = Number(pedidoId);
    const estado = String(farmaciaEstado || '').trim();
    if (!convId || !Number.isFinite(pedidoNum) || pedidoNum <= 0 || !estado) return;

    setConversationList((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const idx = list.findIndex((c) => c.conversation_id === convId);
      const patch = {
        last_pedido_id: pedidoNum,
        last_pedido_farmacia_estado: estado,
        pedidos_count: Math.max(Number(list[idx]?.pedidos_count || 0), 1)
      };
      if (idx === -1) {
        return [
          {
            conversation_id: convId,
            phone: activeSimPhoneDigits,
            fase: 'activa',
            mensajes_count: 0,
            ...patch
          },
          ...list
        ];
      }
      return list.map((c) => (c.conversation_id === convId ? { ...c, ...patch } : c));
    });
  }, [activeSimPhoneDigits]);

  useEffect(() => {
    if (!canAccess) return;
    setLoadingConversations(true);
    syncConversations();
    syncPedidosFarmacia();
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        syncConversations();
        syncPedidosFarmacia();
      }
    }, 35000);
    return () => clearInterval(intervalId);
  }, [canAccess, syncConversations, syncPedidosFarmacia]);

  useEffect(() => {
    if (!canAccess || !chatOpen || !activeConvId) return;
    syncConversations();
  }, [canAccess, chatOpen, activeConvId, syncConversations]);

  useEffect(() => {
    if (!activeConvId) {
      setProfileOpenSection(null);
      return;
    }
    const hasPedido = Boolean(
      lastPedidoId || sessionSnapshot?.last_pedido_id || pedidoDetail?.id
    );
    setProfileOpenSection(hasPedido ? PROFILE_ACCORDION_SECTION.pedido : null);
  }, [activeConvId]);

  useEffect(() => {
    if (!activeConvId) return;
    const hasPedido = Boolean(
      lastPedidoId || sessionSnapshot?.last_pedido_id || pedidoDetail?.id
    );
    setProfileOpenSection((prev) => {
      if (prev === PROFILE_ACCORDION_SECTION.sesion) return prev;
      return hasPedido ? PROFILE_ACCORDION_SECTION.pedido : null;
    });
  }, [activeConvId, lastPedidoId, sessionSnapshot?.last_pedido_id, pedidoDetail?.id]);

  const hydrateConversationData = useCallback((data) => {
    const pedidos = Array.isArray(data?.pedidos) ? data.pedidos : [];
    const mensajes = Array.isArray(data?.mensajes) ? data.mensajes : [];
    const conversation = data?.conversation || {};
    const session = conversation.session || {};
    const convId = String(conversation.conversation_id || '').trim();

    setSessionPedidos(pedidos);
    setSavedMessages(mensajes);
    setSessionSnapshot(session);
    if (convId) {
      setConversationList((prev) =>
        (prev || []).map((c) =>
          c.conversation_id === convId
            ? { ...c, session, phone: conversation.phone || c.phone }
            : c
        )
      );
    }
    setLastPedidoId(session.last_pedido_id || null);
    setMessages(mapDbMessagesToUi(mensajes));

    const lastBot = [...mensajes].reverse().find((m) => m.direction === 'out');
    setLastIntent(lastBot?.meta?.intent || null);
    setShouldHandoff(false);

    const activePedidoId = session.last_pedido_id;
    if (activePedidoId) {
      const fromList = pedidos.find((p) => Number(p.id) === Number(activePedidoId));
      setPedidoDetail(fromList || null);
    } else if (pedidos[0]) {
      setPedidoDetail(pedidos[0]);
    } else {
      setPedidoDetail(null);
    }
  }, []);

  const upsertConversationPhone = useCallback((convId, phoneDigits) => {
    const id = String(convId || '').trim();
    const digits = normalizeSimPhoneDigits(phoneDigits);
    if (!id || !isValidSimPhoneDigits(digits)) return;
    setConversationList((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const idx = list.findIndex((c) => c.conversation_id === id);
      if (idx === -1) {
        return [
          {
            conversation_id: id,
            phone: digits,
            fase: 'activa',
            mensajes_count: 0
          },
          ...list
        ];
      }
      return list.map((c) => (c.conversation_id === id ? { ...c, phone: digits } : c));
    });
  }, []);

  const selectConversation = useCallback(
    async (conversationId) => {
      const convId = String(conversationId || '').trim();
      if (!convId) return;
      setError('');
      setChatOpen(true);
      setActiveConvId(convId);
      const conv = conversationList.find((c) => c.conversation_id === convId);
      const digits = normalizeSimPhoneDigits(conv?.phone);
      if (isValidSimPhoneDigits(digits)) {
        setActiveSimPhoneDigits(digits);
      }
      setLoadingPedidoPanel(true);
      try {
        const data = await fetchAtencionConversacion(convId);
        hydrateConversationData(data);
        const loadedDigits = normalizeSimPhoneDigits(data?.conversation?.phone || conv?.phone);
        if (isValidSimPhoneDigits(loadedDigits)) {
          setActiveSimPhoneDigits(loadedDigits);
        }
      } catch (err) {
        setError(err?.message || 'No se pudo cargar la conversación.');
        setMessages([]);
        setSessionSnapshot({});
        setSessionPedidos([]);
        setSavedMessages([]);
        setPedidoDetail(null);
      } finally {
        setLoadingPedidoPanel(false);
      }
    },
    [conversationList, hydrateConversationData]
  );

  useEffect(() => {
    if (deepLinkConvHandledRef.current) return;
    const convParam = String(searchParams.get('conv') || '').trim();
    if (!convParam) return;
    if (loadingConversations || !conversationList.length) return;

    deepLinkConvHandledRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('conv');
    setSearchParams(next, { replace: true });

    const exists = conversationList.some((c) => c.conversation_id === convParam);
    if (!exists) return;
    selectConversation(convParam);
    setHighlightConvId(convParam);
  }, [
    searchParams,
    conversationList,
    loadingConversations,
    selectConversation,
    setSearchParams
  ]);

  useEffect(() => {
    if (!highlightConvId) return undefined;
    const node = conversationListRef.current?.querySelector(
      `[data-conv-id="${CSS.escape(highlightConvId)}"]`
    );
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightConvId(null), 5200);
    return () => clearTimeout(timer);
  }, [highlightConvId]);

  const refreshPedidoPanel = useCallback(async () => {
    if (!activeConvId) return;
    setLoadingPedidoPanel(true);
    try {
      const data = await fetchAtencionConversacion(convLabel);
      hydrateConversationData(data);
    } catch {
      setSessionPedidos([]);
      setSavedMessages([]);
      if (lastPedidoId || sessionSnapshot?.last_pedido_id) {
        try {
          const pedido = await fetchAtencionPedido(lastPedidoId || sessionSnapshot.last_pedido_id);
          setPedidoDetail(pedido);
        } catch {
          setPedidoDetail(null);
        }
      } else {
        setPedidoDetail(null);
      }
    } finally {
      setLoadingPedidoPanel(false);
    }
  }, [activeConvId, hydrateConversationData, lastPedidoId, sessionSnapshot?.last_pedido_id]);

  const syncPedidoFromResult = useCallback(
    async (result) => {
      const pedidoId = result?.pedido_id;
      if (!pedidoId || !activeConvId) {
        if (result?.session && typeof result.session === 'object') {
          setSessionSnapshot(result.session);
        }
        return;
      }

      const farmaciaEstado =
        result?.farmacia_estado ||
        (result?.intent === 'PEDIDO_CREATE' ? 'pedido_generado_farmacia' : null);
      const optimistic = buildOptimisticPedido(result, activeConvId);

      if (optimistic) {
        setPedidoDetail(optimistic);
        setSessionPedidos((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const idx = list.findIndex((p) => Number(p.id) === Number(optimistic.id));
          if (idx === -1) return [optimistic, ...list];
          return list.map((p, i) => (i === idx ? { ...p, ...optimistic } : p));
        });
      }

      if (farmaciaEstado) {
        patchConversationListFarmacia(activeConvId, pedidoId, farmaciaEstado);
        if (result?.session?.selected_product?.producto) {
          setConversationList((prev) =>
            (prev || []).map((c) =>
              c.conversation_id === activeConvId
                ? { ...c, producto_preview: result.session.selected_product.producto }
                : c
            )
          );
        }
      }

      if (result?.session && typeof result.session === 'object') {
        setSessionSnapshot(result.session);
      }

      try {
        const pedido = await fetchAtencionPedido(pedidoId);
        if (!pedido) return;
        setPedidoDetail(pedido);
        setSessionPedidos((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const idx = list.findIndex((p) => Number(p.id) === Number(pedido.id));
          if (idx === -1) return [pedido, ...list];
          return list.map((p, i) => (i === idx ? { ...p, ...pedido } : p));
        });
        if (pedido.farmacia_estado) {
          patchConversationListFarmacia(activeConvId, pedido.id, pedido.farmacia_estado);
        }
        syncFarmaciaState();
      } catch {
        // El estado optimista ya actualizó la UI.
      }
    },
    [activeConvId, patchConversationListFarmacia, syncFarmaciaState]
  );

  const applyBotResult = useCallback((result, conversationId) => {
    if (result?.intent) setLastIntent(result.intent);
    if (result?.pedido_id) setLastPedidoId(result.pedido_id);
    setShouldHandoff(Boolean(result?.should_handoff));
    if (result?.session && typeof result.session === 'object') {
      setSessionSnapshot(result.session);
      const convId = String(conversationId || '').trim();
      if (convId) {
        setConversationList((prev) =>
          (prev || []).map((c) =>
            c.conversation_id === convId ? { ...c, session: result.session } : c
          )
        );
      }
    }
  }, []);

  const appendMessage = useCallback((entry) => {
    setMessages((prev) => [...prev, entry]);
  }, []);

  const sendMessage = useCallback(
    async (rawText, { silentUser = false, sessionIdOverride, conversationIdOverride } = {}) => {
      const text = String(rawText || '').trim();
      const convId = conversationIdOverride || activeConvId;
      if (!text || sending || !convId) return;
      if (!activeSimPhoneE164) {
        setError('Falta el celular del cliente simulado. Iniciá una nueva conversación.');
        setNewConvPhoneError('');
        setNewConvPhoneDraft('');
        setNewConvModalOpen(true);
        return;
      }

      setError('');
      setSending(true);

      if (!silentUser) {
        appendMessage({
          id: nextMessageId('user'),
          role: 'user',
          text,
          at: new Date()
        });
      }

      try {
        const result = await sendAtencionSimMessage({
          message: text,
          sessionId: sessionIdOverride || toSimSessionId(convId),
          phone: activeSimPhoneE164
        });
        applyBotResult(result, convId);

        const botText = String(result?.response_text || '').trim();
        if (botText) {
          appendMessage({
            id: nextMessageId('bot'),
            role: 'bot',
            text: botText,
            meta: {
              intent: result?.intent,
              pedidoId: result?.pedido_id
            },
            at: new Date()
          });
        } else if (result?.skipped) {
          appendMessage({
            id: nextMessageId('sys'),
            role: 'system',
            text: formatSimSkipMessage(result.reason),
            at: new Date()
          });
        }
        await syncPedidoFromResult(result);
      } catch (err) {
        const msg = err?.message || 'No se pudo contactar al bot de atención.';
        setError(msg);
        appendMessage({
          id: nextMessageId('sys'),
          role: 'system',
          text: msg,
          at: new Date()
        });
      } finally {
        setSending(false);
        inputRef.current?.focus();
      }
    },
    [activeConvId, activeSimPhoneE164, appendMessage, applyBotResult, syncPedidoFromResult, sending]
  );

  const sendAttachment = useCallback(
    async (file) => {
      const convId = activeConvId;
      if (!file || !convId || sending || !activeSimPhoneE164) return;

      const mimeType = String(file.type || 'image/jpeg').toLowerCase();
      if (!RECIPE_ATTACHMENT_TYPES.includes(mimeType)) {
        setError('Formato no soportado. Enviá una foto (JPG/PNG) o un PDF.');
        return;
      }

      setError('');
      setSending(true);
      const previewUrl = mimeType.startsWith('image/') ? URL.createObjectURL(file) : null;
      appendMessage({
        id: nextMessageId('user'),
        role: 'user',
        text: `📎 Receta: ${file.name}`,
        attachment: {
          name: file.name,
          mimeType,
          previewUrl
        },
        at: new Date()
      });

      try {
        const dataBase64 = await fileToBase64(file);
        const result = await sendAtencionBotAttachment({
          sessionId: toSimSessionId(convId),
          phone: activeSimPhoneE164,
          filename: file.name,
          mimeType,
          dataBase64
        });
        applyBotResult(result, convId);

        const botText = String(result?.response_text || '').trim();
        if (botText) {
          appendMessage({
            id: nextMessageId('bot'),
            role: 'bot',
            text: botText,
            meta: { intent: result?.intent },
            at: new Date()
          });
        }
        await syncPedidoFromResult(result);
      } catch (err) {
        const msg = err?.message || 'No se pudo procesar la receta.';
        setError(msg);
        appendMessage({
          id: nextMessageId('sys'),
          role: 'system',
          text: msg,
          at: new Date()
        });
      } finally {
        setSending(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        inputRef.current?.focus();
      }
    },
    [activeConvId, activeSimPhoneE164, appendMessage, applyBotResult, syncPedidoFromResult, sending]
  );

  const sendAudio = useCallback(
    async (file) => {
      const convId = activeConvId;
      if (!file || !convId || sending || !activeSimPhoneE164) return;

      const mimeType = String(file.type || 'audio/ogg').toLowerCase().split(';')[0];
      if (!AUDIO_ATTACHMENT_TYPES.includes(mimeType)) {
        setError('Formato de audio no soportado. Usá OGG, MP3, M4A o WAV.');
        return;
      }

      setError('');
      setSending(true);
      const previewUrl = URL.createObjectURL(file);

      try {
        const dataBase64 = await fileToBase64(file);
        const result = await sendAtencionBotAudio({
          sessionId: toSimSessionId(convId),
          phone: activeSimPhoneE164,
          filename: file.name,
          mimeType,
          dataBase64
        });
        applyBotResult(result, convId);

        const transcription = String(result?.transcription || '').trim();
        appendMessage({
          id: nextMessageId('user'),
          role: 'user',
          text: transcription ? `🎤 "${transcription}"` : '🎤 Audio',
          attachment: {
            name: file.name,
            mimeType,
            previewUrl,
            kind: 'audio'
          },
          meta: transcription ? { intent: 'AUDIO_INPUT' } : undefined,
          at: new Date()
        });

        const botText = String(result?.response_text || '').trim();
        if (botText) {
          appendMessage({
            id: nextMessageId('bot'),
            role: 'bot',
            text: botText,
            meta: { intent: result?.intent },
            at: new Date()
          });
        }
        await syncPedidoFromResult(result);
      } catch (err) {
        const msg = err?.message || 'No se pudo procesar el audio.';
        setError(msg);
        appendMessage({
          id: nextMessageId('sys'),
          role: 'system',
          text: msg,
          at: new Date()
        });
      } finally {
        setSending(false);
        if (audioInputRef.current) audioInputRef.current.value = '';
        inputRef.current?.focus();
      }
    },
    [activeConvId, activeSimPhoneE164, appendMessage, applyBotResult, syncPedidoFromResult, sending]
  );

  const stopAudioRecording = useCallback(
    ({ save }) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') return;

      recorder.onstop = () => {
        setIsRecordingAudio(false);
        const mimeType = recorder.mimeType || 'audio/ogg';
        if (save) {
          const blob = new Blob(recordingChunksRef.current, { type: mimeType });
          if (blob.size > 0) {
            const ext = audioExtensionFromMime(mimeType);
            const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: mimeType });
            void sendAudio(file);
          }
        }
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null;
        }
        mediaRecorderRef.current = null;
        recordingChunksRef.current = [];
      };

      recorder.stop();
    },
    [sendAudio]
  );

  const toggleAudioRecording = useCallback(async () => {
    if (sending || botOnline === false || !activeConvId) return;

    if (isRecordingAudio) {
      stopAudioRecording({ save: true });
      return;
    }

    setError('');
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Tu navegador no soporta grabación de audio.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredTypes = [
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/webm;codecs=opus',
        'audio/webm'
      ];
      const supportedType = preferredTypes.find((type) => {
        try {
          return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
        } catch {
          return false;
        }
      });
      const recorder = supportedType
        ? new MediaRecorder(stream, { mimeType: supportedType })
        : new MediaRecorder(stream);
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) recordingChunksRef.current.push(event.data);
      };
      recorder.start();
      setIsRecordingAudio(true);
    } catch {
      setError('No se pudo acceder al micrófono.');
    }
  }, [activeConvId, botOnline, isRecordingAudio, sending, stopAudioRecording]);

  useEffect(() => {
    return () => {
      if (isRecordingAudio) stopAudioRecording({ save: false });
    };
  }, [isRecordingAudio, stopAudioRecording]);

  const resetConversationState = useCallback(() => {
    setMessages([]);
    setDraft('');
    setLastIntent(null);
    setLastPedidoId(null);
    setShouldHandoff(false);
    setSessionSnapshot({});
    setPedidoDetail(null);
    setSessionPedidos([]);
    setSavedMessages([]);
    setError('');
  }, []);

  const openNewConversationModal = useCallback(() => {
    setNewConvPhoneError('');
    setNewConvPhoneDraft('');
    setNewConvModalOpen(true);
  }, []);

  const closeNewConversationModal = useCallback(() => {
    setNewConvModalOpen(false);
    setNewConvPhoneError('');
  }, []);

  const beginNewConversation = useCallback(
    (phoneDigits) => {
      const digits = normalizeSimPhoneDigits(phoneDigits);
      if (!isValidSimPhoneDigits(digits)) {
        setNewConvPhoneError('Ingresá un celular válido de 10 dígitos (área + número).');
        return false;
      }

      setNewConvPhoneError('');
      setActiveSimPhoneDigits(digits);

      const nextId = newConversationId();
      resetConversationState();
      setActiveConvId(nextId);
      setChatOpen(true);
      upsertConversationPhone(nextId, digits);
      setNewConvModalOpen(false);
      clearAtencionSimSession(toSimSessionId(nextId)).catch(() => { });
      return true;
    },
    [resetConversationState, upsertConversationPhone]
  );

  const confirmNewConversation = useCallback(() => {
    beginNewConversation(newConvPhoneDraft);
  }, [beginNewConversation, newConvPhoneDraft]);

  const catalogPadronHits = useMemo(
    () =>
      Array.isArray(sessionSnapshot?.last_padron_hits) ? sessionSnapshot.last_padron_hits : [],
    [sessionSnapshot?.last_padron_hits]
  );

  const conversationCatalogToken = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role !== 'bot') continue;
      const token = extractCatalogTokenFromText(messages[i]?.text);
      if (token) return token;
    }
    return '';
  }, [messages]);

  const catalogPickerAvailable = useMemo(
    () =>
      catalogPadronHits.length > 1 &&
      !sessionSnapshot?.selected_product &&
      chatOpen &&
      !conversationCatalogToken,
    [
      catalogPadronHits.length,
      sessionSnapshot?.selected_product,
      chatOpen,
      conversationCatalogToken
    ]
  );

  const openCatalogByToken = useCallback(
    (token) => {
      const clean = String(token || '').trim();
      if (!clean || !activeConvId) return;
      markBotFarmaciaCatalogReturn(activeConvId);
      navigate(`/bot-farmacia/t/${clean}`);
    },
    [activeConvId, navigate]
  );

  const openProductCatalog = useCallback(async () => {
    if (!activeConvId || catalogOpening) return;
    const phone = activeSimPhoneE164 || activeContactPhone;
    setCatalogOpening(true);
    setError('');
    try {
      const existingToken = conversationCatalogToken;
      if (existingToken) {
        openCatalogByToken(existingToken);
        return;
      }
      const data = await createAtencionBotCatalogToken(activeConvId, phone, catalogPadronHits);
      const token = String(data?.token || '').trim();
      if (!token) throw new Error('No se pudo generar el enlace al catálogo.');
      openCatalogByToken(token);
    } catch (err) {
      const msg = err?.message || 'No se pudo abrir el catálogo.';
      setError(msg);
    } finally {
      setCatalogOpening(false);
    }
  }, [
    activeContactPhone,
    activeConvId,
    activeSimPhoneE164,
    catalogOpening,
    catalogPadronHits,
    conversationCatalogToken,
    openCatalogByToken
  ]);

  const processCatalogSelections = useCallback(
    async (selections, _padronHits, convId, phoneE164) => {
      if (!Array.isArray(selections) || !selections.length) return;
      const targetConv = convId || activeConvId;
      if (!targetConv) return;

      const phone = phoneE164 || activeContactPhone;
      setCatalogApplying(true);
      setError('');
      if (targetConv !== activeConvId) {
        setActiveConvId(targetConv);
        setChatOpen(true);
      }

      try {
        appendMessage({
          id: nextMessageId('user'),
          role: 'user',
          text: `🛒 Agregué ${selections.length} producto(s) desde el catálogo`,
          at: new Date()
        });

        const result = await applyAtencionBotCatalogSelections(targetConv, phone, selections);
        applyBotResult(result, targetConv);

        const botText = String(result?.response_text || '').trim();
        if (botText) {
          appendMessage({
            id: nextMessageId('bot'),
            role: 'bot',
            text: botText,
            meta: { intent: result?.intent || 'CATALOG_CART' },
            at: new Date()
          });
        }
        await syncPedidoFromResult(result);
      } catch (err) {
        const msg = err?.message || 'No se pudo agregar la selección del catálogo.';
        setError(msg);
        appendMessage({
          id: nextMessageId('sys'),
          role: 'system',
          text: msg,
          at: new Date()
        });
      } finally {
        setCatalogApplying(false);
        inputRef.current?.focus();
      }
    },
    [activeContactPhone, activeConvId, appendMessage, applyBotResult, syncPedidoFromResult]
  );

  useEffect(() => {
    if (!canAccess || catalogReturnHandledRef.current) return;

    const refreshConvId = sessionStorage.getItem('bf_catalog_refresh');
    const result = consumeBotFarmaciaCatalogResult();
    const convId = String(result?.convId || refreshConvId || '').trim();

    if (!convId && !result?.selections?.length) return;

    catalogReturnHandledRef.current = true;
    if (refreshConvId) sessionStorage.removeItem('bf_catalog_refresh');

    void (async () => {
      if (convId) {
        setActiveConvId(convId);
        setChatOpen(true);
        await selectConversation(convId);
      }
      if (result?.selections?.length) {
        await processCatalogSelections(
          result.selections,
          result.padronHits || [],
          convId,
          result.phoneE164
        );
      }
    })();
  }, [canAccess, processCatalogSelections, selectConversation]);

  const goHome = useCallback(() => {
    setChatOpen(false);
    setActiveConvId(null);
    resetConversationState();
  }, [resetConversationState]);

  const handleIngresarFarmacia = useCallback(async () => {
    const pedidoId = pedidoDetail?.id || lastPedidoId || sessionSnapshot?.last_pedido_id;
    if (!pedidoId || confirmingFarmacia) return;
    setError('');
    setConfirmingFarmacia(true);
    try {
      const data = await confirmarAtencionPedidoFarmacia(pedidoId);
      if (data?.pedido) {
        setPedidoDetail(data.pedido);
        setSessionPedidos((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const idx = list.findIndex((p) => Number(p.id) === Number(data.pedido.id));
          if (idx === -1) return [data.pedido, ...list];
          return list.map((p, i) => (i === idx ? { ...p, ...data.pedido } : p));
        });
        if (data.pedido.farmacia_estado && activeConvId) {
          patchConversationListFarmacia(activeConvId, data.pedido.id, data.pedido.farmacia_estado);
        }
        syncFarmaciaState();
      } else if (data?.farmacia?.estado && activeConvId) {
        patchConversationListFarmacia(activeConvId, pedidoId, data.farmacia.estado);
        await refreshPedidoPanel();
      } else {
        await refreshPedidoPanel();
      }
    } catch (err) {
      setError(err?.message || 'No se pudo ingresar el pedido en Farmacia.');
    } finally {
      setConfirmingFarmacia(false);
    }
  }, [
    pedidoDetail?.id,
    lastPedidoId,
    sessionSnapshot?.last_pedido_id,
    confirmingFarmacia,
    activeConvId,
    patchConversationListFarmacia,
    refreshPedidoPanel,
    syncFarmaciaState
  ]);

  const handlePedidoSucursalChange = useCallback(async (pedidoIdRaw, sucursalCodigoRaw) => {
    const pedidoId = Number(pedidoIdRaw);
    if (!Number.isFinite(pedidoId) || pedidoId <= 0) return;
    const sucursalCodigo = String(sucursalCodigoRaw || '').trim();
    const selected = sucursalOptions.find((s) => s.codigo === sucursalCodigo) || null;
    const sucursalPatch = selected ? {
      sucursal_codigo: selected.codigo,
      sucursal_nombre: selected.nombre,
      sucursal: {
        codigo: selected.codigo,
        nombre: selected.nombre,
        direccion: selected.direccion,
        localidad: selected.localidad,
        latitud: selected.latitud,
        longitud: selected.longitud
      }
    } : {
      sucursal_codigo: null,
      sucursal_nombre: null,
      sucursal: null
    };

    const mergeSucursal = (pedido) => (
      Number(pedido?.id) === pedidoId ? { ...pedido, ...sucursalPatch } : pedido
    );

    setError('');
    setSucursalSavingPedidoId(pedidoId);
    setPedidoDetail((prev) => (prev ? mergeSucursal(prev) : prev));
    setSessionPedidos((prev) => (Array.isArray(prev) ? prev.map(mergeSucursal) : prev));

    try {
      const data = await updateAtencionPedidoSucursal(pedidoId, sucursalCodigo);
      const updatedPedido = data?.pedido || null;
      const updatedCard = data?.farmacia || null;
      if (updatedPedido) {
        setPedidoDetail((prev) => (
          Number(prev?.id) === Number(updatedPedido.id) ? { ...prev, ...updatedPedido } : prev
        ));
        setSessionPedidos((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((p) => (Number(p.id) === Number(updatedPedido.id) ? { ...p, ...updatedPedido } : p));
        });
      } else if (updatedCard?.atencion_pedido_id) {
        setSessionPedidos((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((p) => (
            Number(p.id) === Number(updatedCard.atencion_pedido_id)
              ? {
                ...p,
                sucursal_codigo: updatedCard.sucursal_codigo,
                sucursal_nombre: updatedCard.sucursal_nombre,
                sucursal: updatedCard.sucursal
              }
              : p
          ));
        });
      }
      syncFarmaciaState();
    } catch (err) {
      setError(err?.message || 'No se pudo asignar la sucursal del pedido.');
      await refreshPedidoPanel();
    } finally {
      setSucursalSavingPedidoId(null);
    }
  }, [refreshPedidoPanel, sucursalOptions, syncFarmaciaState]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const text = draft;
    setDraft('');
    sendMessage(text);
  };

  const handleReset = async () => {
    if (!chatOpen || !activeConvId) {
      openNewConversationModal();
      return;
    }
    setError('');
    try {
      await clearAtencionSimSession(simSessionId);
      if (isValidSimPhoneDigits(activeSimPhoneDigits)) {
        beginNewConversation(activeSimPhoneDigits);
      } else {
        openNewConversationModal();
      }
    } catch (err) {
      setError(err?.message || 'No se pudo reiniciar la conversación.');
    }
  };

  const activeConvMeta = useMemo(
    () => conversationList.find((c) => c.conversation_id === activeConvId),
    [conversationList, activeConvId]
  );

  const activePedidoId = lastPedidoId || sessionSnapshot?.last_pedido_id || pedidoDetail?.id;

  const chatTitle = useMemo(
    () => conversationChatTitle({ contactDisplayName: simContactDisplayName }),
    [simContactDisplayName]
  );

  const chatSubtitle = useMemo(
    () =>
      buildSimContactSubtitle({
        phone: activeConvMeta?.phone || activeSimPhoneE164,
        pedidoId: activePedidoId || activeConvMeta?.last_pedido_id
      }),
    [activeConvMeta, activeSimPhoneE164, activePedidoId]
  );

  const lastBotMessageId = useMemo(() => {
    const botMsgs = messages.filter((m) => m.role === 'bot');
    return botMsgs.length ? botMsgs[botMsgs.length - 1].id : null;
  }, [messages]);

  if (!canAccess) {
    return (
      <CenteredMessage
        title="Sin acceso a Bot Farmacia"
        message="Pedile a un administrador el permiso ver_bot_farmacia."
      />
    );
  }

  const selectedProduct = sessionSnapshot?.selected_product;
  const intentMeta = lastIntent ? INTENT_META[lastIntent] : null;
  const displayPhone =
    formatWhatsappPhone(activeConvMeta?.phone || activeSimPhoneE164) ||
    formatPhoneDisplay(activeSimPhoneDigits);
  const pedidoMainItem = (pedidoDetail?.items || [])[0] || null;
  const hasPedidoPanel = Boolean(lastPedidoId || sessionSnapshot?.last_pedido_id || pedidoDetail);
  const activePedidoMetodoEntrega = String(pedidoDetail?.metodo_entrega || '').trim().toLowerCase();
  const activePedidoSucursalCodigo = String(pedidoDetail?.sucursal_codigo || pedidoDetail?.sucursal?.codigo || '').trim();
  const activePedidoSucursalNombre = String(pedidoDetail?.sucursal_nombre || pedidoDetail?.sucursal?.nombre || '').trim();
  const showSucursalAssignment = Boolean(
    pedidoDetail?.id &&
    (activePedidoMetodoEntrega === 'domicilio' || activePedidoMetodoEntrega === 'mostrador' || pedidoDetail?.farmacia_estado)
  );
  const sucursalSavingActive = Number(sucursalSavingPedidoId) === Number(pedidoDetail?.id);
  const incomingAuthorLabel = buildIncomingMessageAuthor({
    displayName: simContactDisplayName,
    phoneRaw: activeConvMeta?.phone || activeSimPhoneE164 || activeSimPhoneDigits
  });

  let profileHeadTitle = simContactDisplayName || 'Cliente simulado';
  if (hasPedidoPanel && activePedidoId && pedidoMainItem?.producto) {
    profileHeadTitle = pedidoMainItem.producto;
  } else if (selectedProduct?.producto && !simContactDisplayName) {
    profileHeadTitle = selectedProduct.producto;
  }

  let profileHeadSub = buildSimContactSubtitle({
    phone: activeConvMeta?.phone || activeSimPhoneE164,
    pedidoId: hasPedidoPanel ? activePedidoId : null
  });
  if (hasPedidoPanel && activePedidoId) {
    profileHeadSub = activeConvFarmaciaEstado
      ? `${displayPhone} · Pedido #${activePedidoId} · En Farmacia`
      : `${displayPhone} · Pedido #${activePedidoId} · Mostrador`;
  }

  let conversationListEmptyState = null;
  if (!loadingConversations && filteredConversations.length === 0) {
    const term = listSearch.trim();
    if (term) {
      conversationListEmptyState = {
        icon: '🔎',
        title: 'Sin coincidencias',
        hint: conversationList.length
          ? `Ninguna conversación coincide con «${shortSearchTerm(term)}». Probá producto, pedido # o teléfono.`
          : `No hay conversaciones guardadas para «${shortSearchTerm(term)}».`,
        clearSearch: true
      };
    } else {
      conversationListEmptyState = {
        icon: '💬',
        title: 'Sin conversaciones',
        hint: 'Usá «Nueva conversación» para iniciar una simulación con el bot.'
      };
    }
  }

  const newConvPreviewDigits = normalizeSimPhoneDigits(newConvPhoneDraft);
  const newConvPreviewE164 = simPhoneDigitsToE164(newConvPreviewDigits);
  const newConvCanSubmit = isValidSimPhoneDigits(newConvPreviewDigits);

  return (
    <section className="crm-cw-page crm-bf-page">
      <div
        className={`crm-bf-catalog-checkout-overlay crm-bf-catalog-checkout-overlay--page${catalogApplying ? ' is-visible' : ''
          }`}
        role="status"
        aria-live="polite"
        aria-hidden={!catalogApplying}
        aria-busy={catalogApplying}
      >
        <div className="crm-bf-catalog-checkout-overlay__card">
          <div className="crm-bf-catalog-public-spinner" aria-hidden="true" />
          <p className="crm-bf-catalog-checkout-overlay__title">Agregando al carrito…</p>
          <p className="crm-bf-catalog-checkout-overlay__hint">Anita está preparando tu checklist</p>
        </div>
      </div>

      <div
        className={`crm-bf-newconv-overlay${newConvModalOpen ? ' is-open' : ''}`}
        role="presentation"
        onClick={closeNewConversationModal}
      >
        <div
          className="crm-bf-newconv-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crm-bf-newconv-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crm-bf-newconv-modal__glow" aria-hidden="true" />
          <header className="crm-bf-newconv-modal__head">
            <div className="crm-bf-newconv-modal__icon" aria-hidden="true">
              ✨
            </div>
            <div className="crm-bf-newconv-modal__copy">
              <p className="crm-bf-newconv-modal__eyebrow">Simulador local · dry-run</p>
              <h2 id="crm-bf-newconv-title" className="crm-bf-newconv-modal__title">
                Nueva conversación
              </h2>
              <p className="crm-bf-newconv-modal__sub">
                Ingresá el celular del cliente a simular. El bot lo usará para identificarlo
                como en WhatsApp, hasta tener el número productivo de Meta.
              </p>
            </div>
            <button
              type="button"
              className="crm-bf-newconv-modal__close"
              onClick={closeNewConversationModal}
              aria-label="Cerrar"
            >
              ×
            </button>
          </header>

          <div className="crm-bf-newconv-modal__body">
            <label className="crm-bf-newconv-field">
              <span className="crm-bf-newconv-field__label">Celular del cliente</span>
              <div className={`crm-bf-newconv-phone${newConvPhoneError ? ' has-error' : ''}`}>
                <span className="crm-bf-newconv-phone__prefix" aria-hidden="true">
                  +54 9
                </span>
                <input
                  type="tel"
                  className="crm-bf-newconv-phone__input"
                  value={newConvPhoneDraft}
                  onChange={(e) => {
                    setNewConvPhoneDraft(e.target.value.replace(/[^\d\s-]/g, ''));
                    if (newConvPhoneError) setNewConvPhoneError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmNewConversation();
                    }
                  }}
                  placeholder="261 123 4567"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="tel"
                />
              </div>
              <span className="crm-bf-newconv-field__hint">
                10 dígitos: código de área + número, sin 0 ni 15.
              </span>
              {newConvPreviewE164 ? (
                <span className="crm-bf-newconv-field__preview">
                  WhatsApp simulado: <strong>{formatPhoneDisplay(newConvPreviewDigits)}</strong>
                </span>
              ) : null}
              {newConvPhoneError ? (
                <span className="crm-bf-newconv-field__error" role="alert">
                  {newConvPhoneError}
                </span>
              ) : null}
            </label>
          </div>

          <footer className="crm-bf-newconv-modal__foot">
            <button
              type="button"
              className="crm-bf-btn crm-bf-btn--ghost"
              onClick={closeNewConversationModal}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="crm-bf-btn crm-bf-btn--primary"
              onClick={confirmNewConversation}
              disabled={!newConvCanSubmit}
            >
              Iniciar simulación
            </button>
          </footer>
        </div>
      </div>

      {botOnline === false ? (
        <div className="crm-cw-error-banner crm-bf-error-banner">
          <span>
            Bot offline en <code>{ATENCION_BOT_BASE_URL}</code>.
            <br />
            1) uvicorn en :2000 · 2) Redis: <code>docker compose up redis -d</code>
          </span>
          <button type="button" className="crm-cw-error-banner__retry" onClick={checkHealth}>
            Reintentar
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="crm-cw-error-banner crm-bf-error-banner">
          <span>{error}</span>
          <button type="button" className="crm-cw-error-banner__retry" onClick={() => setError('')}>
            Cerrar
          </button>
        </div>
      ) : null}

      {/* <div className="crm-bf-topbar">
        <div>
          <p className="crm-bf-topbar__eyebrow">Atención mostrador · FARMA GSA</p>
          <h1 className="crm-bf-topbar__title">Bot Farmacia</h1>
        </div>
        <div className="crm-bf-topbar__actions">
          <span
            className={`crm-bf-pill crm-bf-pill--${botOnline ? 'online' : botOnline === false ? 'offline' : 'pending'}`}
          >
            {botOnline ? 'Bot online' : botOnline === false ? 'Bot offline' : 'Verificando…'}
          </span>
          {chatOpen && activeConvFarmaciaEstado ? (
            <span className="crm-bf-pill crm-bf-pill--farmacia">Farmacia</span>
          ) : null}
          {chatOpen ? (
            <span className={`crm-bf-pill crm-bf-pill--phase crm-bf-pill--${flowPhase.tone}`}>
              {flowPhase.label}
            </span>
          ) : null}
          {chatOpen ? (
            <button type="button" className="crm-bf-btn crm-bf-btn--ghost" onClick={goHome}>
              Inicio
            </button>
          ) : null}
          <button type="button" className="crm-bf-btn crm-bf-btn--ghost" onClick={handleReset}>
            Nueva conversación
          </button>
          <Link to="/contact-center" className="crm-bf-btn crm-bf-btn--ghost">
            Ir a gestión de pedidos
          </Link>
        </div>
      </div> */}

      <div className={`crm-cw-layout-two crm-bf-layout${chatOpen ? '' : ' crm-bf-layout--home'}${listCollapsed ? ' is-list-collapsed' : ''}`}>
        <aside className={`crm-cw-list crm-bf-list${listCollapsed ? ' is-collapsed' : ''}`}>
          {listCollapsed ? (
            <div className="crm-bf-list-rail">
              <button
                type="button"
                className="crm-bf-list-rail__toggle"
                onClick={() => setListCollapsed(false)}
                title="Expandir lista de conversaciones"
                aria-label="Expandir lista de conversaciones"
              >
                <span className="crm-bf-list-rail__toggle-icon" aria-hidden="true">»</span>
                <span className="crm-bf-list-rail__toggle-count">{filteredConversations.length}</span>
              </button>
              <div className="crm-bf-list-rail__dock">
                {filteredConversations.map((conv) => {
                  const isActive = chatOpen && conv.conversation_id === activeConvId;
                  const isHighlighted = highlightConvId === conv.conversation_id;
                  const rowTitle = conversationRowDisplayName(conv, {
                    isActive,
                    activeDisplayName: simContactDisplayName,
                    fallbackPhoneE164: activeSimPhoneE164
                  });
                  const initial = String(rowTitle || '?').charAt(0).toUpperCase();
                  return (
                    <button
                      key={`rail-${conv.conversation_id}`}
                      type="button"
                      className={`crm-bf-list-rail__avatar${isActive ? ' is-active' : ''}${isHighlighted ? ' is-bot-return' : ''}`}
                      title={rowTitle}
                      aria-label={rowTitle}
                      onClick={() => selectConversation(conv.conversation_id)}
                    >
                      <span className="crm-bf-list-rail__avatar-ini" aria-hidden="true">{initial}</span>
                      {conv.mensajes_count ? (
                        <span className="crm-bf-list-rail__avatar-badge">{conv.mensajes_count}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div className="crm-cw-list-toolbar">
            <CwListSearch
              inputRef={listSearchRef}
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              placeholder="Buscar conversación…"
            />
            <button
              type="button"
              className="crm-bf-list-collapse"
              onClick={() => setListCollapsed(true)}
              title="Colapsar lista de conversaciones"
              aria-label="Colapsar lista de conversaciones"
            >
              <span aria-hidden="true" style={{ marginTop: "-13px", position: "absolute", marginLeft: "-5px" }}>«</span>
            </button>
          </div>

          <div className="crm-bf-list-section-label">Conversaciones</div>
          <div className="crm-cw-list-scroll crm-bf-conversations" ref={conversationListRef}>
            <button
              type="button"
              className={`crm-cw-item crm-bf-item crm-bf-item--new ${chatOpen && activeConvId && !conversationList.some((c) => c.conversation_id === activeConvId)
                ? 'active'
                : ''
                }`}
              onClick={openNewConversationModal}
            >
              <span className="crm-bf-item__avatar" aria-hidden="true">
                ✨
              </span>
              <span className="crm-bf-item__body">
                <span className="crm-bf-item__title">Nueva conversación</span>
                <span className="crm-bf-item__sub">Simulación local · ingresá el celular</span>
              </span>
            </button>

            {loadingConversations ? (
              <div className="crm-cw-list-empty crm-bf-list-empty" role="status" aria-live="polite" aria-busy="true">
                <span className="crm-cw-list-empty__icon" aria-hidden="true">
                  ⏳
                </span>
                <p className="crm-cw-list-empty__title">Cargando conversaciones</p>
                <p className="crm-cw-list-empty__hint">Sincronizando sesiones del bot…</p>
              </div>
            ) : conversationListEmptyState ? (
              <div className="crm-cw-list-empty crm-bf-list-empty" role="status" aria-live="polite">
                <span className="crm-cw-list-empty__icon" aria-hidden="true">
                  {conversationListEmptyState.icon}
                </span>
                <p className="crm-cw-list-empty__title">{conversationListEmptyState.title}</p>
                <p className="crm-cw-list-empty__hint">{conversationListEmptyState.hint}</p>
                {conversationListEmptyState.clearSearch ? (
                  <div className="crm-cw-list-empty__actions">
                    <button
                      type="button"
                      className="crm-cw-list-empty__btn"
                      onClick={() => {
                        setListSearch('');
                        listSearchRef.current?.focus();
                      }}
                    >
                      Limpiar búsqueda
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const isActive = chatOpen && conv.conversation_id === activeConvId;
                const isHighlighted = highlightConvId === conv.conversation_id;
                const farmaciaEstado =
                  conv.last_pedido_farmacia_estado ||
                  (isActive ? pedidoDetail?.farmacia_estado : null);
                return (
                  <button
                    key={conv.conversation_id}
                    type="button"
                    data-conv-id={conv.conversation_id}
                    className={`crm-cw-item crm-bf-item crm-bf-item--conv ${isActive ? 'active' : ''}${isHighlighted ? ' is-bot-return' : ''}`}
                    onClick={() => selectConversation(conv.conversation_id)}
                  >
                    <span className="crm-bf-item__avatar" aria-hidden="true">
                      💬
                    </span>
                    <span className="crm-bf-item__body">
                      <span className="crm-bf-item__title">
                        {conversationRowDisplayName(conv, {
                          isActive,
                          activeDisplayName: simContactDisplayName,
                          fallbackPhoneE164: activeSimPhoneE164
                        })}
                      </span>
                      <span className="crm-bf-item__sub">{conversationListSubtitle(conv)}</span>
                    </span>
                    <span className="crm-bf-item__meta">
                      {farmaciaEstado ? (
                        <span className="crm-bf-item__badge crm-bf-item__badge--farmacia">
                          Farmacia
                        </span>
                      ) : null}
                      {conv.last_pedido_id ? (
                        <span className="crm-bf-item__badge crm-bf-item__badge--pedido">
                          #{conv.last_pedido_id}
                        </span>
                      ) : null}
                      {conv.mensajes_count ? (
                        <span className="crm-bf-item__badge">{conv.mensajes_count}</span>
                      ) : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {chatOpen && sessionPedidos.length ? (
            <div className="crm-bf-session-pedidos">
              <div className="crm-bf-session-pedidos__head">
                <span className="crm-bf-session-pedidos__label">Pedidos de la sesión</span>
                <span className="crm-bf-session-pedidos__count">{sessionPedidos.length}</span>
              </div>
              <div className="crm-bf-session-pedidos__list">
                {sessionPedidos.map((pedido) => {
                  const meta = pedidoStatusMeta(pedido.status, pedido.farmacia_estado);
                  const pedidoItems = pedido.items || [];
                  const mainItem = pedidoItems[0];
                  const multipleItems = pedidoItems.length > 1;
                  const isActive = Number(activePedidoId) === Number(pedido.id);
                  const inFarmacia = Boolean(pedido.farmacia_estado);
                  const pedidoMetodoEntrega = String(pedido.metodo_entrega || '').trim().toLowerCase();
                  const pedidoSucursalNombre = String(pedido.sucursal_nombre || pedido.sucursal?.nombre || '').trim();
                  return (
                    <button
                      key={pedido.id}
                      type="button"
                      className={`crm-bf-session-pedido-card${isActive ? ' is-active' : ''}${inFarmacia ? ' is-farmacia' : ''
                        }`}
                      onClick={() => navigate(`/contact-center?focus=${pedido.id}`)}
                      title="Abrir en el tablero de Farmacia"
                    >
                      <div className="crm-bf-session-pedido-card__row">
                        <span className="crm-bf-session-pedido-card__id">#{pedido.id}</span>
                        {inFarmacia ? (
                          <span className="crm-bf-session-pedido-card__badge">Farmacia</span>
                        ) : (
                          <span
                            className={`crm-bf-session-pedido-card__status crm-bf-session-pedido-card__status--${meta.tone}`}
                          >
                            {meta.label}
                          </span>
                        )}
                      </div>
                      <span className="crm-bf-session-pedido-card__product">
                        {multipleItems
                          ? `${pedidoItems.length} productos`
                          : mainItem?.producto || 'Sin producto'}
                      </span>
                      {!multipleItems && mainItem?.presentacion ? (
                        <span className="crm-bf-session-pedido-card__presentacion">
                          {mainItem.presentacion}
                        </span>
                      ) : null}
                      {pedidoMetodoEntrega === 'domicilio' || pedidoSucursalNombre ? (
                        <span className={`crm-bf-session-pedido-card__branch${pedidoSucursalNombre ? '' : ' is-pending'}`}>
                          {pedidoSucursalNombre || 'Sucursal pendiente'}
                        </span>
                      ) : null}
                      {!multipleItems && mainItem?.precio_unitario != null ? (
                        <span className="crm-bf-session-pedido-card__price">
                          ${formatPedidoPrice(mainItem.precio_unitario)}
                        </span>
                      ) : null}
                      <span className="crm-bf-session-pedido-card__cta">
                        Abrir en Farmacia
                        <span aria-hidden="true">↗</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

        </aside>

        {!chatOpen ? (
          <section className="crm-bf-home" aria-label="Inicio Bot Farmacia">
            <div className="crm-bf-home__card">
              <div className="crm-bf-home__visual" aria-hidden="true">
                <span className="crm-bf-home__icon crm-bf-home__icon--back">💊</span>
                <span className="crm-bf-home__icon crm-bf-home__icon--front">🛒</span>
              </div>
              <h2 className="crm-bf-home__title">Bot Farmacia · simulador</h2>
              <p className="crm-bf-home__text">
                Elegí una conversación del listado o empezá una <strong>nueva conversación</strong> para
                simular un cliente en mostrador. Todo queda guardado en local, sin WhatsApp productivo.
              </p>
              <div className="crm-bf-home__actions">
                <button type="button" className="crm-bf-btn crm-bf-btn--primary" onClick={openNewConversationModal}>
                  ✨ Nueva conversación
                </button>
              </div>
              {conversationList.length ? (
                <p className="crm-bf-home__hint">
                  {conversationList.length}{' '}
                  {conversationList.length === 1 ? 'conversación guardada' : 'conversaciones guardadas'}{' '}
                  · click en el listado para abrir
                </p>
              ) : null}
            </div>
          </section>
        ) : (
          <>
            <section className={`crm-cw-chat crm-bf-chat ${messages.length === 0 ? 'crm-cw-chat--empty crm-bf-chat--empty' : ''}`}>
              <div className="crm-cw-chat-head">
                <div className="crm-cw-chat-head-nav">
                  <button
                    type="button"
                    className="crm-cw-chat-back"
                    onClick={goHome}
                    aria-label="Volver al listado de conversaciones"
                    title="Volver al listado"
                  >
                    <CwIconChevronPrev />
                    <span className="crm-cw-chat-back__label">Chats</span>
                  </button>
                </div>
                <div className="crm-cw-chat-head-body">
                  <div className="crm-cw-chat-head-row">
                    <div className="crm-cw-chat-head-start">
                      <div className="crm-bf-chat-avatar-wrap">
                        <img src={BOT_AVATAR} alt="" className="crm-bf-chat-avatar" />
                        <span className={`crm-bf-chat-avatar__dot ${botOnline ? 'is-online' : ''}`} />
                      </div>
                      <div>
                        <div className="crm-cw-chat-head-name">{chatTitle}</div>
                        <div className="crm-cw-chat-head-subtitle">{chatSubtitle}</div>
                      </div>
                    </div>
                    <div className="crm-bf-chat-head-badges">
                      {activeConvFarmaciaEstado ? (
                        <span className="crm-bf-mini-badge crm-bf-mini-badge--farmacia">Ingresado en Farmacia</span>
                      ) : null}
                      <span className="crm-bf-mini-badge">Sin WhatsApp</span>
                      <span className="crm-bf-mini-badge crm-bf-mini-badge--soft">Local</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="crm-cw-chat-body crm-bf-chat-body" ref={listRef}>
                {messages.length === 0 ? (
                  <div className="crm-cw-chat-placeholder crm-bf-chat-start" role="status" aria-live="polite">
                    <div className="crm-cw-chat-placeholder__card crm-bf-chat-start__card">
                      <div className="crm-cw-chat-placeholder__visual" aria-hidden="true">
                        <span className="crm-cw-chat-placeholder__bubble crm-cw-chat-placeholder__bubble--back">
                          🛒
                        </span>
                        <span className="crm-cw-chat-placeholder__bubble crm-cw-chat-placeholder__bubble--front">
                          💊
                        </span>
                        <span className="crm-cw-chat-placeholder__orbit" />
                      </div>
                      <h2 className="crm-cw-chat-placeholder__title">Simulá un cliente en mostrador</h2>
                      <p className="crm-cw-chat-placeholder__text">
                        Escribí el primer mensaje como si fueras un cliente en Farmacia GSA. El bot
                        responde en tiempo real y la sesión queda guardada en local.
                      </p>
                      <div className="crm-cw-chat-placeholder__hint crm-bf-chat-start__hint">
                        <span className="crm-cw-chat-placeholder__hint-icon" aria-hidden="true">
                          ↓
                        </span>
                        <span>Usá el campo de abajo para enviar tu primer mensaje</span>
                      </div>
                      <div className="crm-bf-chat-start__chips">
                        {['Hola, buen día', 'Busco un shampoo', '¿Cuánto sale?'].map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            className="crm-bf-chat-start__chip"
                            onClick={() => {
                              setDraft(suggestion);
                              inputRef.current?.focus();
                            }}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  messages.map((msg) => {
                    if (msg.role === 'system') {
                      return (
                        <div key={msg.id} className="crm-cw-activity-event" role="status">
                          <span className="crm-cw-activity-badge">{msg.text}</span>
                        </div>
                      );
                    }
                    const isIncoming = msg.role === 'user';
                    const pedidoDelivered = isPedidoMessageDeliveredToFarmacia(msg, {
                      pedidoId: activePedidoId,
                      farmaciaEstado: activeConvFarmaciaEstado
                    });
                    return (
                      <div
                        key={msg.id}
                        className={`crm-cw-msg ${isIncoming ? 'in' : 'out'} ${msg.id.endsWith(String(messages.length)) ? '' : ''
                          }`}
                      >
                        <div className={`crm-cw-msg-author-row ${isIncoming ? 'in' : 'out'}`}>
                          {!isIncoming ? (
                            <img src={BOT_AVATAR} alt="" className="crm-cw-msg-agent-avatar" loading="lazy" />
                          ) : null}
                          <div className="crm-cw-msg-author">
                            {isIncoming ? incomingAuthorLabel : 'Anita'}
                          </div>
                        </div>
                        {msg.role === 'bot' ? (
                          <>
                            {(() => {
                              const prepared = prepareBotMessageText(msg.text);
                              const catalogToken =
                                prepared.token ||
                                (messageReferencesPreviousCatalog(msg.text)
                                  ? conversationCatalogToken
                                  : '');
                              return (
                                <>
                                  <div
                                    className="crm-cw-msg-content"
                                    dangerouslySetInnerHTML={{
                                      __html: formatBotText(prepared.text)
                                    }}
                                  />
                                  {catalogToken ? (
                                    <div className="crm-bf-msg-catalog-badge-wrap">
                                      <BotCatalogBadge
                                        disabled={catalogOpening}
                                        onClick={() => openCatalogByToken(catalogToken)}
                                      />
                                    </div>
                                  ) : null}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <div className="crm-cw-msg-content">
                            {msg.attachment?.kind === 'audio' && msg.attachment?.previewUrl ? (
                              <audio
                                controls
                                preload="none"
                                src={msg.attachment.previewUrl}
                                className="crm-bf-msg-audio"
                              />
                            ) : null}
                            {msg.attachment?.previewUrl && msg.attachment?.kind !== 'audio' ? (
                              <img
                                src={msg.attachment.previewUrl}
                                alt={msg.attachment.name || 'Receta'}
                                className="crm-bf-msg-attachment"
                              />
                            ) : null}
                            {msg.attachment && !msg.attachment.previewUrl ? (
                              <span className="crm-bf-msg-attachment-file" aria-hidden="true">
                                📄 {msg.attachment.name || 'PDF'}
                              </span>
                            ) : null}
                            <span>{msg.text}</span>
                          </div>
                        )}
                        <div className="crm-bf-msg-foot">
                          {msg.meta?.pedidoId ? (
                            <span className="crm-bf-msg-pedido-chip">
                              <span className="crm-bf-msg-pedido-chip__label">🛒 Pedido</span>
                              <time
                                className="crm-bf-msg-pedido-chip__time"
                                dateTime={msg.at?.toISOString?.()}
                              >
                                {formatTime(msg.at)}
                              </time>
                              <span
                                className={`crm-bf-msg-delivery-check${pedidoDelivered ? ' is-delivered' : ''}`}
                                aria-label={pedidoDelivered ? 'Pedido ingresado en Farmacia' : 'Pedido enviado'}
                              >
                                {pedidoDelivered ? '✓✓' : '✓'}
                              </span>
                            </span>
                          ) : (

                            <span
                              className="crm-bf-msg-meta-chip"
                              style={{
                                display: 'flex',
                                justifyContent: 'end',
                                alignItems: 'center',
                                width: '100%',
                              }}
                            >
                              {/* {msg.meta?.intent ? (() => {
                                const im = intentDisplayMeta(msg.meta.intent);
                                if (!im) return null;
                                return (
                                  <span className={`crm-bf-msg-intent crm-bf-msg-intent--${im.tone}`}>
                                    {im.icon ? `${im.icon} ` : ''}
                                    {im.label}
                                  </span>
                                );
                              })() : null} */}

                              <time
                                className="crm-bf-msg-meta-chip__time"
                                dateTime={msg.at?.toISOString?.()}
                              >
                                {formatTime(msg.at)}
                              </time>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {sending ? (
                  <div className="crm-bf-typing">
                    <span className="crm-bf-typing__dots" aria-hidden="true">
                      <i />
                      <i />
                      <i />
                    </span>
                    Bot Farmacia está respondiendo…
                  </div>
                ) : null}
              </div>

              <div className="crm-cw-chat-foot">
                {catalogPickerAvailable ? (
                  <div className="crm-bf-catalog-banner">
                    <div className="crm-bf-catalog-banner__copy">
                      <span className="crm-bf-catalog-banner__eyebrow">Listado de Anita</span>
                      <strong>{catalogPadronHits.length} opciones disponibles</strong>
                      <span>Seleccioná en el catálogo y volvé al chat con un clic.</span>
                    </div>
                    <button
                      type="button"
                      className="crm-bf-catalog-banner__btn"
                      onClick={openProductCatalog}
                      disabled={catalogOpening}
                    >
                      {catalogOpening ? 'Abriendo…' : 'Abrir catálogo'}
                    </button>
                  </div>
                ) : null}
                <div className="crm-cw-composer-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="crm-bf-attach-input"
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void sendAttachment(file);
                    }}
                  />
                  <input
                    ref={audioInputRef}
                    type="file"
                    className="crm-bf-attach-input"
                    accept="audio/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void sendAudio(file);
                    }}
                  />
                  <button
                    type="button"
                    className="crm-bf-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending || botOnline === false || !activeConvId || isRecordingAudio}
                    title="Enviar foto o PDF de receta"
                    aria-label="Enviar foto o PDF de receta"
                  >
                    📎
                  </button>
                  <button
                    type="button"
                    className={`crm-bf-mic-btn ${isRecordingAudio ? 'is-recording' : ''}`}
                    onClick={() => void toggleAudioRecording()}
                    disabled={sending || botOnline === false || !activeConvId}
                    title={isRecordingAudio ? 'Detener y enviar audio' : 'Grabar audio'}
                    aria-label={isRecordingAudio ? 'Detener y enviar audio' : 'Grabar audio'}
                  >
                    {isRecordingAudio ? '⏹' : '🎤'}
                  </button>
                  <textarea
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (draft.trim() && !sending) handleSubmit(e);
                      }
                    }}
                    placeholder="Escribí como cliente…"
                    className="crm-cw-input"
                    rows={1}
                    disabled={sending || botOnline === false}
                  />
                  <button
                    type="button"
                    className={`crm-cw-send ${sending ? 'is-sending' : ''}`}
                    onClick={handleSubmit}
                    disabled={!draft.trim() || sending || botOnline === false}
                    title="Enviar"
                    aria-label="Enviar mensaje"
                  >
                    {sending ? (
                      <span className="crm-cw-send-spinner" aria-hidden="true" />
                    ) : (
                      <span className="crm-cw-send-icon" aria-hidden="true">
                        ➤
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </section>

            <aside className="crm-cw-profile crm-bf-profile">
              <div className="crm-cw-profile-scroll">
                <div className={`crm-bf-profile-status crm-bf-profile-status--${flowPhase.tone}`}>
                  <div className="crm-bf-profile-status__avatar" aria-hidden="true">
                    <span className="crm-bf-profile-status__avatar-mark">FG</span>
                  </div>
                  <div className="crm-bf-profile-status__body">
                    <div className="crm-bf-profile-status__row">
                      <h2 className="crm-bf-profile-status__title">{formatProductLabel(profileHeadTitle)}</h2>
                      {intentMeta ? (
                        <span
                          className={`crm-bf-profile-status__intent crm-bf-profile-status__intent--${intentMeta.tone}`}
                        >
                          {intentMeta.label}
                        </span>
                      ) : null}
                    </div>
                    <p className="crm-bf-profile-status__sub">{profileHeadSub}</p>
                  </div>
                </div>

                {selectedProduct && !hasPedidoPanel ? (
                  <details
                    className="crm-cw-profile-section crm-cw-profile-details"
                    open={profileOpenSection === PROFILE_ACCORDION_SECTION.producto}
                  >
                    <summary onClick={openProfileAccordionSection(PROFILE_ACCORDION_SECTION.producto)}>
                      Producto en consulta
                    </summary>
                    <div className="crm-cw-profile-details__body">
                      <BfPedidoProductHero
                        producto={selectedProduct.producto}
                        presentacion={selectedProduct.presentacion}
                        precio={selectedProduct.precio}
                      />
                      <div className="crm-bf-pedido-hero__stock">
                        <span>Stock disponible</span>
                        <strong>{selectedProduct.stock_q ?? 0} u.</strong>
                      </div>
                    </div>
                  </details>
                ) : null}

                {hasPedidoPanel ? (
                  <details
                    className="crm-cw-profile-section crm-cw-profile-details"
                    open={profileOpenSection === PROFILE_ACCORDION_SECTION.pedido}
                  >
                    <summary onClick={openProfileAccordionSection(PROFILE_ACCORDION_SECTION.pedido)}>
                      {(pedidoDetail?.items || []).length > 1
                        ? `Pedido #${activePedidoId || '—'} · ${pedidoDetail.items.length} productos`
                        : pedidoMainItem?.producto
                          ? `Pedido #${activePedidoId || '—'} · ${formatProductLabel(pedidoMainItem.producto)}`
                          : `Pedido #${activePedidoId || '—'}`}
                    </summary>
                    <div className="crm-cw-profile-details__body">
                      {loadingPedidoPanel && !pedidoDetail ? (
                        <div className="crm-cw-plex-loading__hint">Consultando pedido guardado…</div>
                      ) : pedidoDetail ? (
                        <div className="crm-bf-pedido-panel">
                          {activeConvFarmaciaEstado ? (
                            <Link
                              to={activePedidoId ? `/contact-center?focus=${activePedidoId}` : '/contact-center'}
                              className="crm-bf-farmacia-ribbon"
                              title="Abrir tablero de Farmacia"
                              aria-label="Abrir tablero de Farmacia"
                            >
                              <span className="crm-bf-farmacia-ribbon__icon" aria-hidden="true">
                                <span className="crm-bf-farmacia-ribbon__icon-ring" />
                                ✓
                              </span>
                              <span className="crm-bf-farmacia-ribbon__copy">
                                <strong>En tablero de Farmacia</strong>
                                <span>{`AT-${activePedidoId} · Pedido ingresado`}</span>
                              </span>
                            </Link>
                          ) : (
                            <div className="crm-bf-farmacia-pending">
                              <p>Confirmá con «sí» para ingresar el pedido en Farmacia.</p>
                              <button
                                type="button"
                                className="crm-bf-btn crm-bf-btn--primary crm-bf-farmacia-pending__btn"
                                onClick={handleIngresarFarmacia}
                                disabled={confirmingFarmacia}
                              >
                                {confirmingFarmacia ? 'Ingresando…' : 'Ingresar en Farmacia'}
                              </button>
                            </div>
                          )}
                          <label className="crm-bf-sucursal-assignment__field">
                            <span>
                              {activePedidoMetodoEntrega === 'domicilio'
                                ? 'Sucursal de envío'
                                : 'Sucursal de retiro'}
                            </span>

                            <select
                              value={selectedSucursal[pedidoDetail.id] ?? activePedidoSucursalCodigo}
                              onChange={(e) =>
                                setSelectedSucursal((prev) => ({
                                  ...prev,
                                  [pedidoDetail.id]: e.target.value,
                                }))
                              }
                              disabled={loadingSucursales || sucursalSavingActive}
                            >
                              <option value="">
                                {loadingSucursales
                                  ? 'Cargando sucursales…'
                                  : 'Sin sucursal asignada'}
                              </option>

                              {sucursalOptions.map((sucursal) => (
                                <option key={sucursal.codigo} value={sucursal.codigo}>
                                  {sucursal.nombre}
                                  {sucursal.localidad ? ` · ${sucursal.localidad}` : ''}
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              className="crm-bf-btn crm-bf-btn--primary"
                              disabled={
                                loadingSucursales ||
                                sucursalSavingActive ||
                                !selectedSucursal[pedidoDetail.id] ||
                                selectedSucursal[pedidoDetail.id] === activePedidoSucursalCodigo
                              }
                              onClick={() =>
                                handlePedidoSucursalChange(
                                  pedidoDetail.id,
                                  selectedSucursal[pedidoDetail.id]
                                )
                              }
                              style={{ height: '30px' }}
                            >
                              {sucursalSavingActive ? 'Guardando…' : 'Guardar'}
                            </button>
                          </label>
                          {(pedidoDetail.items || []).length > 1 ? (
                            <BfPedidoItemsList
                              items={pedidoDetail.items}
                              creado={formatDateTimeCompact(pedidoDetail.created_at)}
                            />
                          ) : pedidoMainItem ? (
                            <BfPedidoProductHero
                              producto={pedidoMainItem.producto}
                              presentacion={pedidoMainItem.presentacion}
                              precio={pedidoMainItem.precio_unitario}
                              codigo={pedidoMainItem.idproducto}
                              creado={formatDateTimeCompact(pedidoDetail.created_at)}
                            />
                          ) : null}
                          {pedidoDetail.notas &&
                            !/^(si|sí|dale|ok|confirmo)$/i.test(String(pedidoDetail.notas).trim()) ? (() => {
                              const notasUi = getPedidoStructuredNotas(pedidoDetail.notas, pedidoDetail);
                              return (
                                <div className="crm-bf-pedido-note">
                                  <span>Notas</span>
                                  {notasUi.plainText ? <p>{notasUi.plainText}</p> : null}
                                  {notasUi.hasStructuredDelivery ? (
                                    <PedidoDeliveryNote
                                      notas={pedidoDetail.notas}
                                      latitud={pedidoDetail.latitud}
                                      longitud={pedidoDetail.longitud}
                                      direccion={pedidoDetail.direccion_envio}
                                      metodoEntrega={pedidoDetail.metodo_entrega}
                                      sucursal={pedidoDetail?.sucursal?.nombre || pedidoDetail?.sucursal_nombre || notasUi.sucursal}
                                      sucursalDireccion={pedidoDetail?.sucursal?.direccion}
                                      sucursalLocalidad={pedidoDetail?.sucursal?.localidad}
                                      sucursalLatitud={pedidoDetail?.sucursal?.latitud}
                                      sucursalLongitud={pedidoDetail?.sucursal?.longitud}
                                    />
                                  ) : null}
                                </div>
                              );
                            })() : null}
                        </div>
                      ) : (
                        <div className="crm-cw-med-empty">
                          Pedido #{activePedidoId} registrado; sincronizando detalle…
                        </div>
                      )}
                    </div>
                  </details>
                ) : null}


              </div>
            </aside>
          </>
        )}
      </div>
    </section>
  );
}
